import * as React from 'react';
import { INewPage, IPage, PageStatus } from 'src/Entities/Pages';
import reducer, { initialState, IPagesState, Action } from './reducer';
import { IPageNode } from './types';
import * as Api from 'src/helpers/api';
import { AxiosError } from 'axios';

const sortByPageOrder = (left: IPage, right: IPage): number => {
  if (left.pageOrder > right.pageOrder) {
    return 1;
  } else if (left.pageOrder < right.pageOrder) {
    return -1;
  }

  return 0;
};

export interface IPagesContext {
  state: typeof initialState;
  list: IPage[];
  listNodes: IPageNode[];
  fetchList: () => void;
  fetchPageBySlug: (slug: string) => Promise<IPage>;
  createPage: (attributes: INewPage) => Promise<IPage>;
  updatePage: (page: IPage) => Promise<IPage>;
  deletePage: (id: IPage['portalPageId']) => Promise<void>;
  reorderPages: (pages: IPage[]) => Promise<void>;
}

const Context = React.createContext({} as IPagesContext);

/**
 * Pages Container
 */
export interface IPagesContainerProps {
  children?: React.ReactNode;
}

export const PagesContainer = (props: IPagesContainerProps) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);

  const list: IPage[] = React.useMemo(() => {
    return state.list.ids.map(id => state.entities[id]).sort(sortByPageOrder);
  }, [state.entities, state.list]);

  const listNodes: IPageNode[] = React.useMemo(() => {
    const nodeMap: { [key: string]: IPageNode } = {};
    // Build each node
    const nodes = list
      .filter(page => page.status === PageStatus.PUBLISHED)
      .map(
        (page): IPageNode => {
          const node = { ...page, children: [] };
          nodeMap[page.portalPageId] = node;
          return node;
        }
      );

    // Map nodes
    nodes.forEach(node => {
      if (node.parentPageId) {
        node.parentNode = nodeMap[node.parentPageId];
      }
      node.children = nodes.filter(
        childNode => childNode.parentPageId === node.portalPageId
      );
    });

    return nodes;
  }, [list]);

  const fetchList = React.useCallback(async () => {
    dispatch({ type: 'FETCH_LIST_REQUEST' });

    const pages = await Api.fetchPages();
    dispatch({ type: 'FETCH_LIST_SUCCESS', pages });
  }, [dispatch]);

  const fetchPageBySlug = React.useCallback(
    async slug => {
      const page = await Api.fetchPageBySlug(slug);
      dispatch({ type: 'UPSERT_PAGE', page });
      return page;
    },
    [dispatch]
  );

  const createPage = React.useCallback(
    attributes => {
      return Api.addPage(attributes).then(page => {
        dispatch({ type: 'ADD_PAGE', page });
        return Promise.resolve(page);
      });
    },
    [dispatch]
  );

  const updatePage = React.useCallback(
    page => {
      return Api.updatePage(page).then(() => {
        dispatch({ type: 'UPDATE_PAGE', page });
        return Promise.resolve(page);
      });
    },
    [dispatch]
  );

  const deletePage = React.useCallback(
    async id => {
      await Api.deletePage(id);
      dispatch({ type: 'DELETE_PAGE', id });
    },
    [dispatch]
  );

  const reorderPages = React.useCallback(
    pages => {
      dispatch({ type: 'FETCH_LIST_SUCCESS', pages });
      return Api.reorderPages(pages);
    },
    [dispatch]
  );

  return (
    <Context.Provider
      value={{
        state,
        list,
        listNodes,
        fetchList,
        fetchPageBySlug,
        createPage,
        updatePage,
        deletePage,
        reorderPages
      }}
      {...props}
    />
  );
};

/**
 * usePages hook
 */
export const usePages = () => React.useContext(Context);

/**
 * usePageTree
 */
export const usePageTree = (): IPageNode[] => {
  const { listNodes } = React.useContext(Context);

  return React.useMemo(() => listNodes.filter(node => !node.parentPageId), [
    listNodes,
    status
  ]);
};

/**
 * useSinglePage
 */
interface IUsePageNode {
  page: IPageNode | null;
  error: Error | AxiosError | null;
}
export const usePageNode = (slug: string): IUsePageNode => {
  const [error, setError] = React.useState<null | Error>(null);
  const { listNodes, fetchPageBySlug } = React.useContext(Context);

  const page = React.useMemo(() => listNodes.find(node => node.slug === slug), [
    listNodes,
    slug
  ]);

  React.useEffect(() => {
    setError(null);

    fetchPageBySlug(slug).catch(fetchError => {
      setError(fetchError);
    });
  }, [slug]);

  return {
    page: page || null,
    error
  };
};



// WEBPACK FOOTER //
// ./src/context/pages/index.tsx